import React, { JSX, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import LzXBaseField from "../BaseField/BaseField";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { debounce } from '@mui/material/utils';
import useLzFApiCall from "@lz/services/Api";
import { lzFIsArray } from "@lz/services/Type";
import { LzTOpenObject } from "@lz/typings/types";
import LzMOption from "@lz/models/commons/Option";

import "./TagsField.scss";

export interface LzPTagsField {
    fluid?: boolean,
    required?: boolean,
    lazyLoad?: boolean,
    placeholder?: string,
    label?: string,
    name?: string,
    icon?: JSX.Element,
    id?: string,
    className?: string,
    style?: React.CSSProperties,
    value?: LzMOption[],
    size?: 'small' | 'medium',
    options?: LzMOption[],
    refTableName?: string,
    refTableIdField?: string,
    refTableTitleField?: string,
    refFilterFieldKeys?: string[];
    modelToFilter?: LzTOpenObject;
    queryVarName?: string,
    maxLimit?: number,
    noFirstOption?: boolean,
    firstOptionValue?: string,
    firstOptionText?: string,
    onChange?: (name: string, value: LzMOption[]) => void
    filterOptionsFunction?: (options: LzMOption[]) => LzMOption[],
}

const LzXTagsField = (props: LzPTagsField) => {

    if (typeof props.value === 'undefined') {
        return <CircularProgress />;
    }

    const loadingFromDBAttempted = React.useRef(false);

    const [options, setOptions] = useState<LzMOption[] | null>(typeof props.options === 'undefined' ? null : props.options);
    const [filteredOptions, setFilteredOptions] = useState<LzMOption[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [opened, setOpened] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const lzApiCall = useLzFApiCall();

    const setFitleredOptionsWithFiltering = (options: LzMOption[] | null) => {
        if (options === null) {
            setFilteredOptions(null);
        } else {
            if (typeof props.filterOptionsFunction === 'undefined') {
                setFilteredOptions(options);
            } else {
                setFilteredOptions(
                    props.filterOptionsFunction(options)
                );
            }
        }
    };

    const loadOptions = async (searchQuery?: string) => {
        if (loading) {
            return;
        }
        if (props.refTableName && props.refTableName != '') {
            setLoading(true);
            setErrorMessage('');

            let params = { max: props.maxLimit || 0, [props.queryVarName || 'q']: searchQuery || inputValue };

            // Only send filters to server if its lazyLoad (and not instant load)
            if (props.lazyLoad && props.refFilterFieldKeys && props.modelToFilter) {
                const filters: LzTOpenObject = {};
                console.log('FindFilters', props.refFilterFieldKeys, props.modelToFilter);
                for (const filterFieldKey of props.refFilterFieldKeys) {
                    if (typeof props.modelToFilter[filterFieldKey] !== 'undefined') {
                        filters[filterFieldKey] = props.modelToFilter[filterFieldKey];
                    }
                }
                params = Object.assign({}, params, filters);
            }

            const data = await lzApiCall('options/' + props.refTableName.replace(/_/g, '-'), params);

            // Load data if call was successful
            if (data) {
                const newOptions = LzMOption.parseArray(data, props.refTableIdField || 'id', props.refTableTitleField || 'name', props.refFilterFieldKeys);
                setOptions(newOptions);
                if (props.lazyLoad) {
                    setFitleredOptionsWithFiltering(newOptions);
                }
            } else {
                setErrorMessage('Cannot load options, please view logs...');
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        // If options not provided
        if (typeof props.options === 'undefined') {
            setOptions(null);

            if (props.lazyLoad) {
                if (opened) {
                    loadOptions();
                }
            } else {
                if (!loadingFromDBAttempted.current) {
                    loadOptions();
                    loadingFromDBAttempted.current = true;
                }
            }
        }

        // If options provided then use them
        else {
            setOptions(props.options);
        }
    }, [props.options]);

    const debounceLoadOptions = useMemo(
        () =>
            debounce((searchQuery: string) => loadOptions(searchQuery), 400),
        [],
    );

    useEffect(() => {
        if (opened) {
            if (props.lazyLoad) {
                loadOptions();
            }
        }
    }, [opened]);

    useEffect(() => {
        if (opened) {
            if (props.lazyLoad) {
                debounceLoadOptions(inputValue);
            }
        }
    }, [inputValue]);

    // useLzEffectAfterMount() does not work if the select field is in some content that is toggled for display (like Dialog)
    useEffect(() => {
        // If options not set then filtered options are null as well
        if (!options) {
            setFitleredOptionsWithFiltering(props.lazyLoad ? [] : null);
            return;
        }

        // If its in lazyLoad mode then filtered options are the ones that come from server, so just exit
        if (props.lazyLoad) {
            return;
        }

        // Build filtered options
        const temp: LzMOption[] = [];
        for (const option of options) {
            if (props.refFilterFieldKeys && props.refFilterFieldKeys.length > 0 && props.modelToFilter) {
                for (const filterFieldKey of props.refFilterFieldKeys) {
                    if (
                        typeof props.modelToFilter[filterFieldKey] === 'undefined' ||
                        typeof option[filterFieldKey] === 'undefined' ||
                        props.modelToFilter[filterFieldKey] === option[filterFieldKey]
                    ) {
                        temp.push(option);
                    }
                }
            } else {
                temp.push(option);
            }
        }
        // console.log('Filtered-Opts', temp);
        setFitleredOptionsWithFiltering(temp);
    }, [props.refFilterFieldKeys, props.modelToFilter, options]);

    const isReady = filteredOptions && lzFIsArray(filteredOptions);

    const theName = props.name || '';
    const theId = props.id || 'id_' + theName;

    const handleOnChange = (val: LzMOption[]): void => {
        if (props.onChange) {
            props.onChange(theName, val);
        }
    };

    return (
        <LzXBaseField
            labelShrink
            id={theId}
            fluid={props.fluid}
            required={props.required}
            icon={props.icon}
            name={props.name}
            label={props.label}
        >

            {!isReady &&
                <div className='lz-select'>
                    {loading && <CircularProgress />}
                    {errorMessage !== '' && <Alert variant="filled" color="warning">{errorMessage}</Alert>}
                </div>
            }

            {isReady &&
                <Autocomplete multiple filterSelectedOptions
                    open={opened}
                    onOpen={() => setOpened(true)}
                    onClose={() => setOpened(false)}
                    fullWidth={props.fluid}
                    size={props.size || "small"}
                    id={theId}
                    options={filteredOptions}
                    getOptionLabel={(option) => option.text}
                    isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                    value={props.value}
                    className={'lz-tags ' + (props.className ? props.className : '')}
                    style={props.style || {}}
                    filterOptions={
                        // Only assign this if lazy load is active, so that it handles it by itself when not in lazy load mode
                        props.lazyLoad ?
                            (x) => x
                            :
                            undefined
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            required={props.required}
                            name={props.name || ''}
                            placeholder={props.placeholder || ''}
                            autoComplete="password"
                        />
                    )}
                    onChange={(evt, val, act) => handleOnChange(val)}
                    onInputChange={
                        // Only assign this if lazy load is active, so that it handles it by itself when not in lazy load mode
                        props.lazyLoad ?
                            (_, newInputValue) => {
                                setInputValue(newInputValue);
                            }
                            :
                            undefined
                    }
                />
            }

        </LzXBaseField>
    );
};

export default LzXTagsField;
